import { format } from 'date-fns';

// ─── Role types ────────────────────────────────────────────────────────────────

export type EmployeeRoleType =
    | 'frontend-developer'
    | 'backend-developer'
    | 'qa'
    | 'csops'
    | 'devops'
    | null;

// ─── Role helpers ─────────────────────────────────────────────────────────────

export const getRoleFromEmployeeRole = (employeeRole: string): EmployeeRoleType => {
    const role = (employeeRole || '').toLowerCase();
    // Check specific combinations before generic substrings
    if (role.includes('frontend') && role.includes('developer')) return 'frontend-developer';
    if (role.includes('backend') && role.includes('developer')) return 'backend-developer';
    if (role.includes('devops') || role.includes('dev ops')) return 'devops';
    if (role.includes('qa') || role.includes('quality') || role.includes('test')) return 'qa';
    if (
        role.includes('csops') ||
        role.includes('cs ops') ||
        role.includes('cs_ops') ||
        role.includes('customer success')
    )
        return 'csops';
    return null;
};

// Developer task type names (still used as a reference for task lookups)
export const DEVELOPER_TASK_TYPES = [
    'Frontend/Database Resource implements fix',
    'Backend Resource diagnoses root cause',
];

/**
 * Returns task type + name defaults for a given role.
 * Used by AddDefectTask to pre-fill task dropdowns when a pod is auto-selected.
 */
export const getTaskDefaultsByRole = (role: EmployeeRoleType, screenName: string) => {
    switch (role) {
        case 'frontend-developer':
            return {
                task_type: DEVELOPER_TASK_TYPES[0],
                task_name: `${screenName} Frontend implements fix`,
            };
        case 'backend-developer':
            return {
                task_type: DEVELOPER_TASK_TYPES[1],
                task_name: `${screenName} Backend diagnoses root cause`,
            };
        case 'qa':
            return {
                task_type: 'QA Resource reproduces bug with logs',
                task_name: `${screenName} QA regression`,
            };
        case 'csops':
            return {
                task_type: 'CS Resource reports customer impact',
                task_name: `${screenName} Customer confirms in their own words`,
            };
        default:
            return { task_type: '', task_name: '' };
    }
};

// ─── Bug defaults ─────────────────────────────────────────────────────────────

// DevOps template task type name — update to match the actual goal template if different
export const DEVOPS_TASK_TYPE_NAME = 'DevOps Resource deploys to production';

export const BUG_TASK_TYPE_NAMES = [
    'Frontend/Database Resource implements fix',
    'Backend Resource diagnoses root cause',
    'QA Resource reproduces bug with logs',
    'CS Resource reports customer impact',
    DEVOPS_TASK_TYPE_NAME,
];

export const DEFAULT_BUG_SKILL_NAMES = ['Python', 'React', 'Azure', 'Web Application Testing','Communication Skills','Leadership'];

// ─── Auto-creation internals ──────────────────────────────────────────────────

// Maps template task type name → which role pool handles it
const AUTO_TASK_ROLE_MAP: Record<string, EmployeeRoleType> = {
    'Frontend/Database Resource implements fix': 'frontend-developer',
    'Backend Resource diagnoses root cause': 'backend-developer',
    'QA Resource reproduces bug with logs': 'qa',
    'CS Resource reports customer impact': 'csops',
    [DEVOPS_TASK_TYPE_NAME]: 'devops',
};

// Task title used when auto-creating (one title per single-task role)
const AUTO_CREATION_TASK_TITLE: Record<string, string> = {
    'frontend-developer': 'Reproduce with evidence; severity set; tags verified against the item',
    'backend-developer': 'Reproduce with evidence; severity set; tags verified against the item',
    'qa': 'QA regression',
    'devops': 'Production release',
    'csops':'Customer confirms in their own words'
};

// CSOps produces two tasks from the same template task type
const CSOPS_AUTO_TASK_TITLES = [
    'Release note',
    'Customer confirms in their own words',
];

// ─── Training Issue defaults ──────────────────────────────────────────────────

export const TRAINING_ISSUE_TASK_TYPE_NAME = 'Training Issue';
export const CUSTOMER_FOLLOWUP_TASK_TYPE_NAME = 'Customer Follow Up';

// ─── Goal static lookup ───────────────────────────────────────────────────────

/** Find the goal_static that owns the bug task types. */
export function findBugGoalStatic(
    goalMasterData: any,
): { id: string; name: string } | null {
    const goals: any[] = goalMasterData?.goal_master_data?.goals_static ?? [];
    // Prefer the goal that has ALL known bug task types
    const full = goals.find((g) => {
        const names: string[] = (g.tasks ?? []).map((t: any) => t.name);
        return BUG_TASK_TYPE_NAMES.every((n) => names.includes(n));
    });
    if (full) return { id: String(full.id), name: full.name };

    // Fallback: goal with at least one bug task type
    const partial = goals.find((g) =>
        (g.tasks ?? []).some((t: any) => BUG_TASK_TYPE_NAMES.includes(t.name)),
    );
    return partial ? { id: String(partial.id), name: partial.name } : null;
}

/** Find the goal_static that owns a "Training Issue" task type. */
export function findTrainingIssueGoalStatic(
    goalMasterData: any,
): { id: string; name: string } | null {
    const goals: any[] = goalMasterData?.goal_master_data?.goals_static ?? [];
    const found = goals.find((g) =>
        (g.tasks ?? []).some((t: any) => t.name === TRAINING_ISSUE_TASK_TYPE_NAME),
    );
    return found ? { id: String(found.id), name: found.name } : null;
}

/** Find the goal_static that owns a "Customer Follow-up" task type. */
export function findCustomerFollowupGoalStatic(
    goalMasterData: any,
): { id: string; name: string } | null {
    const goals: any[] = goalMasterData?.goal_master_data?.goals_static ?? [];
    const found = goals.find((g) =>
        (g.tasks ?? []).some((t: any) => t.name === CUSTOMER_FOLLOWUP_TASK_TYPE_NAME),
    );
    return found ? { id: String(found.id), name: found.name } : null;
}

// ─── Payload builder ──────────────────────────────────────────────────────────

interface SkillOption {
    key: string | number;
    name: string;
}

interface PodMember {
    employee_id: string | number;
    first_name: string;
    last_name: string;
    employee_role: string;
    skill_set: string[];
    is_available: boolean;
}

interface BuildParams {
    defect: {
        id: any;
        screen_name: string;
        agency_name: string;
        description?: string;
    };
    podMembers: PodMember[];
    podId: string | null;
    goalMasterData: any;
    bugGoalStatic: { id: string; name: string };
    selectedSkills: SkillOption[];
    employeeID: string;
}

export function buildBugGoalPayload(
    params: BuildParams,
): { goalData: any; error: null } | { goalData: null; error: string } {
    const {
        defect,
        podMembers,
        podId,
        goalMasterData,
        bugGoalStatic,
        selectedSkills,
        employeeID,
    } = params;

    if (!selectedSkills.length) {
        return { goalData: null, error: 'No skills found for this goal.' };
    }

    const today = format(new Date(), 'yyyy-MM-dd');

    const selectedGoal = (goalMasterData?.goal_master_data?.goals_static ?? []).find(
        (g: any) => String(g.id) === bugGoalStatic.id,
    );

    // Group members by role using the shared getRoleFromEmployeeRole
    const frontendDevMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'frontend-developer',
    );
    const backendDevMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'backend-developer',
    );
    const qaMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'qa',
    );
    const csopsMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'csops',
    );
    const devopsMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'devops',
    );

    const allTasks: any[] = [];
    const memberKeySet = new Set<number>();

    function buildTaskEntry(taskStatic: any, member: PodMember, taskTitle: string) {
        const memberKey = String(member.employee_id);
        const taskID = String(taskStatic.id);
        memberKeySet.add(parseInt(memberKey));

        const checklists = (taskStatic.checklists ?? []).map((i: any) => ({
            task_id: parseInt(taskID),
            checklist_id: parseInt(String(i.id)),
            status: false,
            created_by: parseInt(employeeID),
        }));

        const matchedSkill = selectedSkills.find((s) =>
            (member.skill_set || []).some(
                (ss: string) =>
                    ss.toLowerCase().includes(s.name.toLowerCase()) ||
                    s.name.toLowerCase().includes(ss.toLowerCase()),
            ),
        );

        return {
            is_new_task: true,
            goal_id: 1,
            static_goal_id: parseInt(bugGoalStatic.id),
            static_task_id: parseInt(taskID),
            task_title: taskTitle,
            task_start_date: `${today}T00:00:00.000Z`,
            task_end_date: `${today}T23:59:59.000Z`,
            dependency_task: null,
            created_by: parseInt(employeeID),
            days: 1,
            hours: 1,
            mins: 0,
            checklists,
            assigned_to: parseInt(memberKey),
            skill_ids: matchedSkill ? [parseInt(String(matchedSkill.key))] : [],
        };
    }

    // Iterate over tasks from goal template and assign to the correct role pool
    for (const taskStatic of (selectedGoal?.tasks ?? [])) {
        const requiredRole = AUTO_TASK_ROLE_MAP[taskStatic.name];
        if (!requiredRole) continue;

        if (requiredRole === 'frontend-developer') {
            if (!frontendDevMembers.length) continue;
            allTasks.push(buildTaskEntry(
                taskStatic,
                frontendDevMembers[0],
                AUTO_CREATION_TASK_TITLE['frontend-developer'],
            ));
        } else if (requiredRole === 'backend-developer') {
            if (!backendDevMembers.length) continue;
            allTasks.push(buildTaskEntry(
                taskStatic,
                backendDevMembers[0],
                AUTO_CREATION_TASK_TITLE['backend-developer'],
            ));
        } else if (requiredRole === 'qa') {
            if (!qaMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, qaMembers[0], AUTO_CREATION_TASK_TITLE['qa']));
        }else if (requiredRole === 'devops') {
            if (!devopsMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, devopsMembers[0], AUTO_CREATION_TASK_TITLE['devops']));
        }   else if (requiredRole === 'csops') {
            if (!csopsMembers.length) continue;
        allTasks.push(buildTaskEntry(taskStatic, csopsMembers[0], AUTO_CREATION_TASK_TITLE['csops']));
        }
    }

    const memberKeys = Array.from(memberKeySet);

    if (memberKeys.length < 2) {
        return {
            goalData: null,
            error: `Not enough pod members with known roles to create a goal (found ${memberKeys.length}, need at least 2).`,
        };
    }

    const skillIds = selectedSkills.map((s) => parseInt(String(s.key)));

    const goalData = {
        goal_title: `${defect.agency_name} for issue ${defect.screen_name}`,
        project_id: 1,
        static_goal_id: parseInt(bugGoalStatic.id),
        is_new_goal: true,
        goal_start_date: `${today}T00:00:00.000Z`,
        goal_end_date: `${today}T23:59:59.000Z`,
        created_by: parseInt(employeeID),
        existing_goal_id: null,
        description: defect.description || null,
        goal_members: memberKeys,
        removed_member_ids: [],
        idea_box_goal_id: null,
        tasks: allTasks,
        attachment_list: [],
        links: [],
        skill_ids: skillIds,
        pod_id: podId ? parseInt(podId) : null,
    };

    return { goalData, error: null };
}

// ─── Shared CSops single-task payload builder (private) ──────────────────────

interface _BuildCSOpsParams {
    defect: { id: any; screen_name: string; agency_name: string; description?: string };
    podMembers: PodMember[];
    podId: string | null;
    goalMasterData: any;
    goalStatic: { id: string; name: string };
    selectedSkills: SkillOption[];
    employeeID: string;
    taskTypeName: string;
    taskTitle: string;
    goalTitleInfix: string;
}

function _buildSingleCSOpsTaskGoalPayload(
    params: _BuildCSOpsParams,
): { goalData: any; error: null } | { goalData: null; error: string } {
    const { defect, podMembers, podId, goalMasterData, goalStatic, selectedSkills, employeeID, taskTypeName, taskTitle, goalTitleInfix } = params;

    const csopsMember = podMembers.find(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'csops',
    );
    if (!csopsMember) {
        return {
            goalData: null,
            error: 'No Customer Success (CSops) member found in this pod. Cannot create the goal automatically.',
        };
    }

    const today = format(new Date(), 'yyyy-MM-dd');
    const selectedGoal = (goalMasterData?.goal_master_data?.goals_static ?? []).find(
        (g: any) => String(g.id) === goalStatic.id,
    );
    const taskStatic = (selectedGoal?.tasks ?? []).find((t: any) => t.name === taskTypeName);
    if (!taskStatic) {
        return { goalData: null, error: `${taskTitle} task type not found in goal template.` };
    }

    const memberKey = String(csopsMember.employee_id);
    const taskID = String(taskStatic.id);

    const checklists = (taskStatic.checklists ?? []).map((i: any) => ({
        task_id: parseInt(taskID),
        checklist_id: parseInt(String(i.id)),
        status: false,
        created_by: parseInt(employeeID),
    }));

    const matchedSkill = selectedSkills.find((s) =>
        (csopsMember.skill_set || []).some(
            (ss: string) =>
                ss.toLowerCase().includes(s.name.toLowerCase()) ||
                s.name.toLowerCase().includes(ss.toLowerCase()),
        ),
    );

    const task = {
        is_new_task: true,
        goal_id: 1,
        static_goal_id: parseInt(goalStatic.id),
        static_task_id: parseInt(taskID),
        task_title: taskTitle,
        task_start_date: `${today}T00:00:00.000Z`,
        task_end_date: `${today}T23:59:59.000Z`,
        dependency_task: null,
        created_by: parseInt(employeeID),
        days: 1,
        hours: 0,
        mins: 30,
        checklists,
        assigned_to: parseInt(memberKey),
        skill_ids: matchedSkill ? [parseInt(String(matchedSkill.key))] : [],
    };

    const skillIds = selectedSkills.map((s) => parseInt(String(s.key)));

    const goalData = {
        goal_title: `${defect.agency_name} for ${goalTitleInfix} ${defect.screen_name}`,
        project_id: 1,
        static_goal_id: parseInt(goalStatic.id),
        is_new_goal: true,
        goal_start_date: `${today}T00:00:00.000Z`,
        goal_end_date: `${today}T23:59:59.000Z`,
        created_by: parseInt(employeeID),
        existing_goal_id: null,
        description: defect.description || null,
        goal_members: [parseInt(memberKey)],
        removed_member_ids: [],
        idea_box_goal_id: null,
        tasks: [task],
        attachment_list: [],
        links: [],
        skill_ids: skillIds,
        pod_id: podId ? parseInt(podId) : null,
    };

    return { goalData, error: null };
}

// ─── Training Issue payload builder ──────────────────────────────────────────

interface BuildTrainingIssueParams {
    defect: { id: any; screen_name: string; agency_name: string; description?: string };
    podMembers: PodMember[];
    podId: string | null;
    goalMasterData: any;
    trainingIssueGoalStatic: { id: string; name: string };
    selectedSkills: SkillOption[];
    employeeID: string;
}

export function buildTrainingIssueGoalPayload(
    params: BuildTrainingIssueParams,
): { goalData: any; error: null } | { goalData: null; error: string } {
    return _buildSingleCSOpsTaskGoalPayload({
        defect: params.defect,
        podMembers: params.podMembers,
        podId: params.podId,
        goalMasterData: params.goalMasterData,
        goalStatic: params.trainingIssueGoalStatic,
        selectedSkills: params.selectedSkills,
        employeeID: params.employeeID,
        taskTypeName: TRAINING_ISSUE_TASK_TYPE_NAME,
        taskTitle: 'Training Issue',
        goalTitleInfix: 'training issue',
    });
}

// ─── Customer Follow-up payload builder ──────────────────────────────────────

interface BuildCustomerFollowupParams {
    defect: { id: any; screen_name: string; agency_name: string; description?: string };
    podMembers: PodMember[];
    podId: string | null;
    goalMasterData: any;
    customerFollowupGoalStatic: { id: string; name: string };
    selectedSkills: SkillOption[];
    employeeID: string;
}

export function buildCustomerFollowupGoalPayload(
    params: BuildCustomerFollowupParams,
): { goalData: any; error: null } | { goalData: null; error: string } {
    return _buildSingleCSOpsTaskGoalPayload({
        defect: params.defect,
        podMembers: params.podMembers,
        podId: params.podId,
        goalMasterData: params.goalMasterData,
        goalStatic: params.customerFollowupGoalStatic,
        selectedSkills: params.selectedSkills,
        employeeID: params.employeeID,
        taskTypeName: CUSTOMER_FOLLOWUP_TASK_TYPE_NAME,
        taskTitle: 'Follow-up',
        goalTitleInfix: 'follow-up',
    });
}

// ─── Enhancement defaults ─────────────────────────────────────────────────────

export const ENHANCEMENT_TASK_TYPE_NAMES = [
    'Frontend Resource implements UI in React',
    'Backend Resource checks first and then builds API in Python',
    'QA Resource validates functionality',
    'Database/AI Resource sets schema & logic',
    'Production Release',
];

/** Find the goal_static that owns the Enhancement task types. */
export function findEnhancementGoalStatic(
    goalMasterData: any,
): { id: string; name: string } | null {
    const goals: any[] = goalMasterData?.goal_master_data?.goals_static ?? [];
    const found = goals.find((g) =>
        (g.tasks ?? []).some((t: any) => ENHANCEMENT_TASK_TYPE_NAMES.includes(t.name)),
    );
    return found ? { id: String(found.id), name: found.name } : null;
}

// ─── Enhancement payload builder ──────────────────────────────────────────────

interface BuildEnhancementParams {
    defect: { id: any; screen_name: string; agency_name: string; description?: string };
    podMembers: PodMember[];
    podId: string | null;
    goalMasterData: any;
    goalStatic: { id: string; name: string };
    selectedSkills: SkillOption[];
    employeeID: string;
}

export function buildEnhancementGoalPayload(
    params: BuildEnhancementParams,
): { goalData: any; error: null } | { goalData: null; error: string } {
    const { defect, podMembers, podId, goalMasterData, goalStatic, selectedSkills, employeeID } = params;

    if (!selectedSkills.length) {
        return { goalData: null, error: 'No skills found for this goal.' };
    }

    const today = format(new Date(), 'yyyy-MM-dd');

    const d30 = new Date();
    d30.setDate(d30.getDate() + 30);
    const thirtyDaysLater = format(d30, 'yyyy-MM-dd');

    const d7 = new Date();
    d7.setDate(d7.getDate() + 7);
    const sevenDaysLater = format(d7, 'yyyy-MM-dd');

    const selectedGoal = (goalMasterData?.goal_master_data?.goals_static ?? []).find(
        (g: any) => String(g.id) === goalStatic.id,
    );

    const frontendDevMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'frontend-developer',
    );
    const backendDevMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'backend-developer',
    );
    const qaMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'qa',
    );
    const devopsMembers = podMembers.filter(
        (m) => getRoleFromEmployeeRole(m.employee_role || '') === 'devops',
    );

    const allTasks: any[] = [];
    const memberKeySet = new Set<number>();

    function buildTaskEntry(taskStatic: any, member: PodMember, taskTitle: string) {
        const memberKey = String(member.employee_id);
        const taskID = String(taskStatic.id);
        memberKeySet.add(parseInt(memberKey));

        const checklists = (taskStatic.checklists ?? []).map((i: any) => ({
            task_id: parseInt(taskID),
            checklist_id: parseInt(String(i.id)),
            status: false,
            created_by: parseInt(employeeID),
        }));

        const matchedSkill = selectedSkills.find((s) =>
            (member.skill_set || []).some(
                (ss: string) =>
                    ss.toLowerCase().includes(s.name.toLowerCase()) ||
                    s.name.toLowerCase().includes(ss.toLowerCase()),
            ),
        );

        return {
            is_new_task: true,
            goal_id: 1,
            static_goal_id: parseInt(goalStatic.id),
            static_task_id: parseInt(taskID),
            task_title: taskTitle,
            task_start_date: `${sevenDaysLater}T00:00:00.000Z`,
            task_end_date: `${sevenDaysLater}T23:59:59.000Z`,
            dependency_task: null,
            created_by: parseInt(employeeID),
            days: 1,
            hours: 1,
            mins: 0,
            checklists,
            assigned_to: parseInt(memberKey),
            skill_ids: matchedSkill ? [parseInt(String(matchedSkill.key))] : [],
        };
    }

    const screenName = defect.screen_name;

    for (const taskStatic of (selectedGoal?.tasks ?? [])) {
        if (taskStatic.name === 'Frontend Resource implements UI in React') {
            if (!frontendDevMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, frontendDevMembers[0], `Frontend ${screenName} Enhancement`));
        } else if (taskStatic.name === 'Backend Resource checks first and then builds API in Python') {
            if (!backendDevMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, backendDevMembers[0], `Backend ${screenName} Enhancement`));
        } else if (taskStatic.name === 'QA Resource validates functionality') {
            if (!qaMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, qaMembers[0], ` ${screenName} QA regression`));
        } else if (taskStatic.name === 'Database/AI Resource sets schema & logic') {
            if (!devopsMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, devopsMembers[0], `${screenName} Production Release`));
        } else if (taskStatic.name === 'Production Release') {
            if (!devopsMembers.length) continue;
            allTasks.push(buildTaskEntry(taskStatic, devopsMembers[0], `Production Release ${screenName} Enhancement`));
        }
    }

    const memberKeys = Array.from(memberKeySet);

    if (memberKeys.length < 2) {
        return {
            goalData: null,
            error: `Not enough pod members with known roles to create an Enhancement goal (found ${memberKeys.length}, need at least 2).`,
        };
    }

    const skillIds = selectedSkills.map((s) => parseInt(String(s.key)));

    const goalData = {
        goal_title: `${defect.agency_name} for issue ${defect.screen_name}`,
        project_id: 1,
        static_goal_id: parseInt(goalStatic.id),
        is_new_goal: true,
        goal_start_date: `${today}T00:00:00.000Z`,
        goal_end_date: `${thirtyDaysLater}T23:59:59.000Z`,
        created_by: parseInt(employeeID),
        existing_goal_id: null,
        description: defect.description || null,
        goal_members: memberKeys,
        removed_member_ids: [],
        idea_box_goal_id: null,
        tasks: allTasks,
        attachment_list: [],
        links: [],
        skill_ids: skillIds,
        pod_id: podId ? parseInt(podId) : null,
    };

    return { goalData, error: null };
}
